<div class="entity-config-page tenant-settings-page container-fluid">
  <div class="entity-config-shell tenant-settings-shell d-flex flex-column gap-0 mx-auto position-relative">
    <blocking-loader-overlay
      :show="page_loading"
      :message="page_loading_message"
      :detail="page_loading_detail"
    />
    <section class="entity-config-top tenant-settings-header mb-1">
      <div class="entity-config-top-hero tenant-settings-hero">
        <div class="row g-2 align-items-start">
          <div class="col-12 col-xl-8">
            <div class="tenant-settings-breadcrumb text-uppercase d-flex align-items-center gap-2 mb-2">
              <span>Home</span>
              <span>/</span>
              <span>Tenant Settings</span>
            </div>
            <div class="entity-page-title mb-2 mt-2">Tenant settings</div>

          </div>
          <div class="col-12 col-xl-4">
            <div class="tenant-settings-actions d-flex flex-wrap justify-content-xl-end gap-2">
              <button type="button" class="btn btn-primary-dark tenant-settings-primary-btn" @click="open_tenant_setup_modal">
                + Set up new tenant
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="row g-0">
      <div class="col-12 p-0 m-0">
        <div class="tenant-settings-content border-0 h-100">
          <div class="tenant-settings-tabs d-flex flex-wrap border-bottom">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              type="button"
              class="tenant-settings-tab btn btn-link text-decoration-none rounded-0 border-0 fs-6"
              :class="{ 'is-active': active_tab === tab.key }"
              @click="set_tab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div v-if="active_tab === 'roles'" class="tenant-roles-card bg-white border">
            <div class="tenant-roles-toolbar d-flex align-items-center justify-content-between gap-2">
              <div class="tenant-roles-eyebrow text-uppercase"></div>
              <button
                type="button"
                class="btn tenant-role-add-btn"
                @click="open_create_role_modal"
              >
                + Add role
              </button>
            </div>

            <div class="tenant-roles-table-wrap table-responsive">
              <table class="tenant-roles-table">
                <thead>
                  <tr class="entity-table-head text-uppercase secondary-color">
                    <th>Role</th>
                    <th>Label shown to users</th>
                    <th>Default scope</th>
                    <th>Authorisation cap</th>
                    <th>People</th>
                    <th><span class="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="roles_error">
                    <td colspan="6" class="tenant-roles-state text-center">
                      {{ roles_error }}
                    </td>
                  </tr>
                  <tr v-else-if="!roles_loading && !roles.length">
                    <td colspan="6" class="tenant-roles-state text-center">
                      No roles configured for this tenant yet.
                    </td>
                  </tr>
                  <tr v-for="role in roles" :key="role.id">
                    <td>
                      <span class="tenant-role-badge" :class="role.role_badge_class">
                        {{ role.role_tier }}
                      </span>
                    </td>
                    <td>
                      <div class="tenant-role-title">{{ role.title }}</div>
                      <div class="tenant-role-description">{{ role.description }}</div>
                    </td>
                    <td>
                      <div class="tenant-role-value">{{ role.scope }}</div>
                    </td>
                    <td>
                      <div class="tenant-role-value">{{ role.cap }}</div>
                    </td>
                    <td>
                      <div class="tenant-role-value">{{ role.people }}</div>
                    </td>
                    <td class="text-end">
                      <button type="button" class="btn tenant-role-edit-btn" @click="open_edit_role_modal(role)">Edit</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <case-domains v-else-if="active_tab === 'domains'" :project-id="tenant_project_id" />

          <rule-engine v-else-if="active_tab === 'rules'" :project-id="tenant_project_id" />

          <sla-workflows v-else-if="active_tab === 'sla'" :project-id="tenant_project_id" />

          <rates-billing
            v-else-if="active_tab === 'billing'"
            :roles="role_records"
          />

          <tenant-integrations
            v-else-if="active_tab === 'integrations'"
            :project-id="tenant_project_id"
          />

          <branding-copy v-else-if="active_tab === 'branding'" />

          <div v-else class="entity-vibrant-binding-card rounded-4 bg-white border shadow-sm p-4 text-center text-secondary small">
            {{ active_tab_label }} settings are not configured yet.
          </div>

        </div>
      </div>
    </section>
  </div>

  <div
    v-if="show_tenant_setup_modal"
    class="tenant-setup-backdrop d-flex align-items-center justify-content-center"
    @click.self="close_tenant_setup_modal"
  >
    <section
      class="tenant-setup-modal bg-white d-flex flex-column"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tenant-setup-title"
    >
      <header class="tenant-setup-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h4 id="tenant-setup-title" class="tenant-setup-modal-title mb-2">Set up a new tenant</h4>
          <div class="tenant-setup-step-copy">Step {{ tenant_setup_step }} of 5</div>
        </div>
        <button
          type="button"
          class="tenant-setup-close btn border-0 p-0"
          aria-label="Close tenant setup"
          @click="close_tenant_setup_modal"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="tenant-setup-body">
        <div v-if="tenant_setup_step === 1">
          <h3 class="tenant-setup-section-title mb-1">Tell us about your organisation</h3>
          <p class="tenant-setup-section-copy mb-4">This becomes your tenant identity. You can change branding later.</p>

          <div class="mb-3">
            <label class="tenant-setup-label mb-2" for="tenant_organisation_name">Organisation name</label>
            <input
              id="tenant_organisation_name"
              v-model="tenant_setup_form.organisation_name"
              type="text"
              class="form-control tenant-setup-control"
              placeholder="e.g. Limpopo Legal Aid"
            >
          </div>

          <div class="mb-3">
            <label class="tenant-setup-label mb-2" for="tenant_sector">Sector</label>
            <select id="tenant_sector" v-model="tenant_setup_form.sector" class="form-select tenant-setup-control">
              <option>Insurance · short-term</option>
              <option>Legal services</option>
              <option>Public sector</option>
              <option>Professional services</option>
              <option>Other</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="tenant-setup-label mb-2" for="tenant_primary_contact">Primary contact</label>
            <input
              id="tenant_primary_contact"
              v-model="tenant_setup_form.primary_contact"
              type="text"
              class="form-control tenant-setup-control"
              placeholder="First and last name"
              :disabled="Boolean(tenant_setup_contact_user_id)"
            >
          </div>

          <div>
            <label class="tenant-setup-label mb-2" for="tenant_primary_email">Primary email</label>
            <input
              id="tenant_primary_email"
              v-model="tenant_setup_form.primary_email"
              type="email"
              class="form-control tenant-setup-control"
              placeholder="ops@yourorg.example"
              :disabled="Boolean(tenant_setup_contact_user_id)"
            >
          </div>

          <div class="mt-3">
            <label class="tenant-setup-label mb-2" for="tenant_primary_phone">Primary phone</label>
            <input
              id="tenant_primary_phone"
              v-model="tenant_setup_form.primary_phone"
              type="tel"
              class="form-control tenant-setup-control"
              placeholder="e.g. 0821234567"
              :disabled="Boolean(tenant_setup_contact_user_id)"
            >
            <div v-if="tenant_setup_contact_user_id" class="small text-secondary mt-2">
              This contact has been provisioned and assigned to the tenant.
            </div>
          </div>
        </div>

        <div v-else-if="tenant_setup_step === 2">
          <h3 class="tenant-setup-section-title mb-1">Pick your case domains</h3>
          <p class="tenant-setup-section-copy mb-4">Each domain ships with a starter schema, rules and SLA. You can edit any of them after setup.</p>

          <div v-if="tenant_domains_error && !tenant_domains_loading" class="tenant-setup-domain-state is-error">
            <span>{{ tenant_domains_error }}</span>
            <button type="button" class="btn btn-sm tenant-setup-secondary-btn" @click="load_tenant_domain_options">
              Try again
            </button>
          </div>

          <div v-else class="tenant-setup-choice-grid">
            <label
              v-for="domain in tenant_domain_options"
              :key="domain.key"
              class="tenant-setup-choice-card"
              :class="{ 'is-selected': tenant_setup_form.domains.includes(domain.key) }"
            >
              <input v-model="tenant_setup_form.domains" type="checkbox" :value="domain.key">
              <span>
                <span class="tenant-setup-choice-title">{{ domain.title }}</span>
                <span class="tenant-setup-choice-description">{{ domain.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else-if="tenant_setup_step === 3">
          <h3 class="tenant-setup-section-title mb-1">Choose tenant roles</h3>
          <p class="tenant-setup-section-copy mb-4">Select the roles that should have access to this tenant and its chosen case domains.</p>

          <div v-if="tenant_roles_error && !tenant_roles_loading" class="tenant-setup-domain-state is-error">
            <span>{{ tenant_roles_error }}</span>
            <button type="button" class="btn btn-sm tenant-setup-secondary-btn" @click="load_tenant_role_options">
              Try again
            </button>
          </div>

          <div v-else class="tenant-setup-choice-grid">
            <label
              v-for="role in tenant_role_options"
              :key="role.key"
              class="tenant-setup-choice-card"
              :class="{ 'is-selected': tenant_setup_form.roles.includes(role.key) }"
            >
              <input v-model="tenant_setup_form.roles" type="checkbox" :value="role.key">
              <span>
                <span class="tenant-setup-choice-title">{{ role.title }}</span>
                <span class="tenant-setup-choice-description">{{ role.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else-if="tenant_setup_step === 4">
          <h3 class="tenant-setup-section-title mb-1">Plug in the basics</h3>
          <p class="tenant-setup-section-copy mb-4">You can add the rest later. Skip anything you don't have credentials for.</p>

          <div class="tenant-setup-integration-list">
            <label
              v-for="integration in tenant_integration_options"
              :key="integration.key"
              class="tenant-setup-choice-card tenant-setup-integration-card"
              :class="{ 'is-selected': tenant_setup_form.integrations.includes(integration.key) }"
            >
              <input v-model="tenant_setup_form.integrations" type="checkbox" :value="integration.key">
              <span>
                <span class="tenant-setup-choice-title">{{ integration.title }}</span>
                <span class="tenant-setup-choice-description">{{ integration.description }}</span>
              </span>
            </label>
          </div>
        </div>

        <div v-else>
          <h3 class="tenant-setup-section-title mb-1">All set.</h3>
          <p class="tenant-setup-section-copy mb-4">We'll provision your tenant in about 90 seconds. While we do, you can:</p>

          <div class="tenant-setup-summary-list">
            <div class="tenant-setup-summary-item">
              <strong>Invite your team.</strong> Add coordinators and handlers from CSV or one-by-one.
            </div>
            <div class="tenant-setup-summary-item">
              <strong>Walk through a sample case.</strong> We'll seed 12 demo cases across your domains so you can see the verdict view, time entry and billing flow.
            </div>
            <div class="tenant-setup-summary-item">
              <strong>Customise the rules engine.</strong> 71 starter rules are loaded. Test, edit or write your own.
            </div>
          </div>

          <div class="tenant-setup-provision-note mt-4">
            Provisioning C-{{ current_year }} · case prefix for this year set automatically.
          </div>
        </div>
      </div>

      <footer class="tenant-setup-footer d-flex align-items-center justify-content-end gap-2">
        <button
          v-if="tenant_setup_step === 1"
          type="button"
          class="btn tenant-setup-secondary-btn"
          :disabled="tenant_setup_creating_project"
          @click="close_tenant_setup_modal"
        >
          Cancel
        </button>
        <button
          v-else
          type="button"
          class="btn tenant-setup-secondary-btn"
          :disabled="tenant_setup_creating_project || tenant_roles_saving"
          @click="previous_tenant_setup_step"
        >
          Back
        </button>
        <button
          v-if="tenant_setup_step < 5"
          type="button"
          class="btn btn-primary-dark tenant-setup-primary-btn d-inline-flex align-items-center gap-2"
          :disabled="tenant_setup_creating_project || tenant_roles_saving || (tenant_setup_step === 2 && tenant_domains_loading) || (tenant_setup_step === 3 && tenant_roles_loading)"
          @click="next_tenant_setup_step"
        >
          {{ tenant_setup_creating_project ? 'Creating tenant...' : (tenant_roles_saving ? 'Saving roles...' : 'Continue') }}
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary-dark tenant-setup-primary-btn"
          @click="finish_tenant_setup"
        >
          Finish setup
        </button>
      </footer>
    </section>
  </div>

  <div
    v-if="show_create_role_modal"
    class="company-case-modal-backdrop d-flex align-items-center justify-content-center"
    @click.self="close_create_role_modal"
  >
    <section class="company-case-modal bg-white">
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">Create role</h2>
          <div class="company-case-modal-copy">Add a tenant role that can be assigned to people and workflows.</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close modal" @click="close_create_role_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="tenant_role_name">Role name</label>
          <input
            id="tenant_role_name"
            v-model.trim="create_form.role_name"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Enter role name"
            :disabled="creating_role"
          >
        </div>

        <div class="mb-0">
          <label class="company-case-modal-label text-uppercase mb-2" for="tenant_role_description">Description</label>
          <textarea
            id="tenant_role_description"
            v-model.trim="create_form.role_description"
            class="form-control company-case-modal-control company-case-modal-textarea"
            rows="4"
            placeholder="Enter role description"
            :disabled="creating_role"
          ></textarea>
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="creating_role" @click="close_create_role_modal">Cancel</button>
        <button type="button" class="btn btn-primary-dark px-4 d-inline-flex align-items-center gap-2" :disabled="creating_role" @click="create_role">
          <span v-if="creating_role" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ creating_role ? 'Saving...' : 'Save' }}
        </button>
      </footer>
    </section>
  </div>

  <div
    v-if="show_edit_role_modal"
    class="company-case-modal-backdrop d-flex align-items-center justify-content-center"
    @click.self="close_edit_role_modal"
  >
    <section class="company-case-modal bg-white">
      <header class="company-case-modal-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2 class="company-case-modal-title mb-2">Edit role</h2>
          <div class="company-case-modal-copy">Update the tenant role label and description shown to users.</div>
        </div>
        <button type="button" class="company-case-modal-close btn border-0 p-0" aria-label="Close modal" @click="close_edit_role_modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="company-case-modal-body">
        <div class="mb-3">
          <label class="company-case-modal-label text-uppercase mb-2" for="tenant_edit_role_name">Role name</label>
          <input
            id="tenant_edit_role_name"
            v-model.trim="edit_form.role_name"
            type="text"
            class="form-control company-case-modal-control"
            placeholder="Enter role name"
            :disabled="updating_role"
          >
        </div>

        <div class="mb-0">
          <label class="company-case-modal-label text-uppercase mb-2" for="tenant_edit_role_description">Description</label>
          <textarea
            id="tenant_edit_role_description"
            v-model.trim="edit_form.role_description"
            class="form-control company-case-modal-control company-case-modal-textarea"
            rows="4"
            placeholder="Enter role description"
            :disabled="updating_role"
          ></textarea>
        </div>
      </div>

      <footer class="company-case-modal-footer d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-light border px-4" :disabled="updating_role" @click="close_edit_role_modal">Cancel</button>
        <button type="button" class="btn btn-primary-dark px-4 d-inline-flex align-items-center gap-2" :disabled="updating_role" @click="update_role">
          <span v-if="updating_role" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ updating_role ? 'Saving...' : 'Save Changes' }}
        </button>
      </footer>
    </section>
  </div>
</div>
